import React, { useState, useEffect, createContext } from 'react';
import withKeepTabFocus from '../Containers/withKeepTabFocus';
import classNames from 'classnames';

const context = createContext({});

const { Provider, Consumer } = context;

const OpenModalButton = ({
  id,
  children,
  className,
  clickCallback,
  fullScreen,
  tabIndex,
  ariaLabel,
}) => {
  const clickHandler = (changeModal) => {
    changeModal(id);
    if (clickCallback !== undefined) clickCallback();
    if (fullScreen) document.body.classList.add('no-scroll');
  };

  return (
    <Consumer>
      {({ changeModal, activeModalId }) => (
        <button
          tabIndex={tabIndex}
          data-buttonid={`open${id}`}
          id={`button-${id}--opener`}
          type="button"
          className={classNames({
            [`${className}`]: true,
            [`${className}--open`]: activeModalId === id,
          })}
          aria-label={ariaLabel}
          onClick={() => clickHandler(changeModal)}
        >
          {children}
        </button>
      )}
    </Consumer>
  );
};

const CloseModalButton = ({ children, className, id }) => (
  <Consumer>
    {({ closeModal }) => (
      <button
        autoFocus={id}
        type="button"
        className={className}
        onClick={() => closeModal(id)}
      >
        {children}
      </button>
    )}
  </Consumer>
);

const ModalPanelInner = withKeepTabFocus(({ children, ...props }) => {
  return React.cloneElement(children, { ...props });
});

const ModalPanel = ({ whenActive, children, hideWithCss }) => (
  <Consumer>
    {({ activeModalId }) =>
      activeModalId === whenActive || hideWithCss ? (
        <div
          className={classNames({
            'modal-is-closed': activeModalId !== whenActive,
          })}
        >
          <ModalPanelInner>{children}</ModalPanelInner>
        </div>
      ) : null
    }
  </Consumer>
);

const Modal = (props) => {
  const [activeModalId, setActiveModalId] = useState('');
  const fullScreenModal = props.fullScreen;

  const changeModal = (newModalId) => {
    setActiveModalId(activeModalId === newModalId ? '' : newModalId);
  };

  const closeModal = (id) => {
    setActiveModalId('');
    if (
      props.noScroll === true &&
      document.body.classList.contains('no-scroll')
    )
      document.body.classList.remove('no-scroll');
    if (document.querySelector(`[data-buttonid='open${id}']`))
      document.querySelector(`[data-buttonid='open${id}']`).focus();
  };

  const escCloseHandler = (e) => {
    if (activeModalId !== '') {
      if (e.keyCode === 27) {
        e.preventDefault();
        closeModal();
      }
    }
    return null;
  };

  const handleClick = (e) => {
    if (activeModalId !== '') {
      if (e.target.className === 'category-lists-modal__modal-panel') {
        closeModal();
      }
    }
    return null;
  };

  useEffect(() => {
    document.addEventListener('keydown', (e) => escCloseHandler(e));
    document.addEventListener('mousedown', (e) => handleClick(e));
  }, [activeModalId]);

  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', escCloseHandler());
      document.removeEventListener('mousedown', handleClick());
    };
  }, []);

  return (
    <Provider
      value={{
        activeModalId,
        fullScreenModal,
        changeModal,
        closeModal,
      }}
    >
      {props.children}
    </Provider>
  );
};

Modal.defaultProps = {
  noScroll: true,
};

export default Modal;
export { OpenModalButton, CloseModalButton, ModalPanel, Consumer };
